import {
  ArbitraryTypedObject,
  PortableTextMarkDefinition,
  PortableTextSpan,
} from '@portabletext/types';
import { PortableTextBlock } from 'next-sanity';

/**
 * Extract FAQ questions from various possible locations in the page content
 * This function handles different page structures that might contain FAQ data
 */
export function extractFaqQuestions(pageContent: any): any[] {
  // Check if we have dedicated FAQ field with expected structure
  if (
    Array.isArray(pageContent?.faq) &&
    pageContent.faq.length > 0 &&
    Array.isArray(pageContent.faq[0]?.questions)
  ) {
    return pageContent.faq[0].questions.map(formatFaqQuestion);
  }

  // Check if bodyText directly contains questions array
  if (Array.isArray(pageContent?.bodyText?.questions)) {
    return pageContent.bodyText.questions.map(formatFaqQuestion);
  }

  // Check if bodyText is the FAQ array itself
  if (
    Array.isArray(pageContent?.bodyText) &&
    pageContent.bodyText.some((item: any) => item.question && item.answer)
  ) {
    return pageContent.bodyText.map(formatFaqQuestion);
  }

  // Look for faqFields from schema markup
  if (Array.isArray(pageContent?.faqFields?.questions)) {
    return pageContent.faqFields.questions.map(formatFaqQuestion);
  }

  // If no structured FAQ data found, return empty array
  return [];
}

/**
 * Format a FAQ question object into the proper schema structure
 */
export function formatFaqQuestion(q: any): any {
  // Extract text from Portable Text format if answer is in that format
  let answerText = 'Default Answer Text';

  if (q?.answer) {
    if (Array.isArray(q.answer)) {
      // Handle Portable Text format
      answerText = q.answer
        .map((block: any) => {
          if (block.children && Array.isArray(block.children)) {
            return block.children.map((child: any) => child.text || '').join('');
          }
          return '';
        })
        .join('\n\n');
    } else if (typeof q.answer === 'string') {
      // Handle plain string answer
      answerText = q.answer;
    }
  }

  return {
    '@type': 'Question',
    name: q?.question || 'Default Question Name',
    acceptedAnswer: {
      '@type': 'Answer',
      text: answerText,
    },
  };
}

export function filterFaqFromBodyText(bodyText: any[]): any[] {
  if (!bodyText || !Array.isArray(bodyText)) {
    return [];
  }
  return bodyText.filter(
    (block: any) => block._type === 'faq' || (block._ref && block._id && block.questions),
  );
}

export function cleanQuestionText(question: string): string {
  // Remove numbering patterns like "1. ", "2. ", etc.
  return question.replace(/^\d+\.\s*/, '');
}

export function prepareSchemaFaqContent(bodyText: any | undefined): any {
  const faqBlocks = filterFaqFromBodyText(bodyText || []);

  // Process questions to remove numbering if present
  return faqBlocks.map((block) => {
    // If this is a direct FAQ block
    if (block.questions && Array.isArray(block.questions)) {
      return {
        ...block,
        questions: block.questions.map((q: any) => ({
          ...q,
          question: cleanQuestionText(q.question),
        })),
      };
    }
    return block;
  });
}
